import { createQueryParams } from "features/config/helpers";
import {
  useAppDispatch,
  useAppSelector,
  useQuery,
} from "features/config/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Tab,
  Tabs,
} from "@mui/material";
import { TiDocumentAdd } from "react-icons/ti";
import { ConfirmOrdersModal, PlaceOrderModal, ProcessModal } from "../modals";
import { FaEye } from "react-icons/fa";
import { FcHighPriority } from "react-icons/fc";

import {
  getStockOrders,
  resetGetStockOrders,
  selectGetStockOrders,
} from "../slices/get-stock-orders.slice";
import {
  Column,
  DataTable,
  DataTableCell,
  DataTableRow,
} from "features/shared/presentation/components/data-table";
import { TAB_NAVIGATION } from "features/shared/constants";

import { DataList } from "features/shared/presentation/components";
import { openStockProcess } from "../slices/stock-order.slice";
import {
  dateSetup,
  NoticeBanner,
  tabStyleSettings,
  useActionAvailability,
} from "./stock-ordering-utils";
import { OrderFilter } from ".";
import { CiCalendar, CiFilter } from "react-icons/ci";
import React from "react";
import { resetGetProductData } from "../slices/get-product-data.slice";
import { adminSettingsEditUserMaterialSupplierCustomer } from "features/admin/presentation/slices/admin-settings-edit-user-meterial-supplier-customer.slice";
import { selectStockActiveProcess } from "../slices/stock-active-process.slice";
import { ActiveProcessModel } from "features/stock-ordering/core/domain/actove-process.model";
import { AppDispatch } from "features/config/store";
import {
  getAdminSession,
  selectGetAdminSession,
} from "features/admin/presentation/slices/get-admin-session.slice";
import { selectGetStockOrderStores } from "../slices/get-store.slice";
import { HiOutlineClipboardCheck, HiOutlineShoppingCart } from "react-icons/hi";
import { MdOutlineStorefront } from "react-icons/md";
import { getOrderConfig } from "../slices/get-order-config.slice";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface ListOfActiveStores {
  store_id: string;
  name: string;
  franchise_type_id: string;
}

interface DataFilterData {
  store?: readonly StoreArray[] | null;
  type?: string | null;
  start?: string | null;
  end?: string | null;
}

interface Modals {
  [key: string]: boolean;
}

interface FloatingPlaceOrderProps {
  actionProcess: ActiveProcessModel["process_action_access"] | undefined;
  view: string | undefined;
  modals: Modals;
  dispatch: AppDispatch;
  handleModalToggle: (processName: string) => void;
}

interface DateTimelineProps {
  placement: string;
  requested?: string;
  committed?: string;
  actual?: string;
  next?: string | null;
}

interface OrderContentsProps {}
interface StoreArray extends Array<ListOfActiveStores> {}

export function OrderContents() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  /**
   * view param
   */
  const { view } = useParams();

  /**
   * Selectors
   */
  const getAdminSessionState = useAppSelector(selectGetAdminSession);
  const getStockOrdersState = useAppSelector(selectGetStockOrders);
  const stockActiveProcessState = useAppSelector(selectStockActiveProcess);

  const { process, process_action_access } = stockActiveProcessState.data ?? {};

  /**
   * Custom hook for getting process id
   */
  const { processId } = useActionAvailability();

  /**
   * Modal trigger state
   */
  const getStoreState = useAppSelector(selectGetStockOrderStores);

  const [modals, setModals] = useState<Modals>({
    placeOrder: false,
    confirmOrder: false,
    processModal: false,
  });

  /**
   * Filter trigger state
   */
  const [openFilter, setOpenFilter] = useState<HTMLButtonElement | null>(null);

  /**
   * Query Parameters
   */
  const query = useQuery();
  const pageNo = query.get("page_no");
  const perPage = query.get("per_page");
  const orderBy = query.get("order_by");
  const ordr = query.get("order");

  const search = query.get("search");
  const status = query.get("status");
  const store = query.get("store");
  const tabValue = query.get("tab");

  const dateType = query.get("dateType");
  const startDate = query.get("startDate");
  const endDate = query.get("endDate");

  /**
   * Table columns
   */

  let columns: Array<Column> = [
    { id: "store_name", label: "Store/Company" },
    { id: "supplierId", label: "Supplier" },
    { id: "id", label: "Order Number" },
    { id: "orderStatus", label: "Order Status" },
    { id: "order_placement_date", label: "Key Dates" },
    // { id: "order_placement_date", label: "Order Placement Date" },
    // {
    //   id: "requested_delivery_date",
    //   label: "Requested Delivery Date",
    // },
    // { id: "commited_delivery_date", label: "Commited Delivery Date" },
    // { id: "actual_delivery_date", label: "Actual Delivery Date" },
    { id: "action", label: "Action" },
  ];

  /**
   * This fetch orders
   */

  useEffect(() => {
    if (processId) {
      const query = createQueryParams({
        page_no: pageNo,
        per_page: perPage,
        order_by: orderBy,
        order: ordr,
        store: store,
        search: search,
        dateType: dateType,
        startDate: startDate,
        endDate: endDate,
        tab: tabValue,
      });

      if (
        (!getStockOrdersState?.data || tabValue === null) &&
        process_action_access
      ) {
        const queryParams = createQueryParams({
          page_no: pageNo, //parseInt(pageNo) ?? 0 default value,
          per_page: perPage, //parseInt(perPage) ?? 25 default value,
          order_by: orderBy, //orderBy ?? "last_updated" default value,
          order: ordr, //order ?? "asc" default value,
          store: store, //store or company,
          search: search, //search,
          dateType: dateType, //dateType,
          startDate: startDate, //startDate,
          endDate: endDate, //endDate,
          tab: 0,
        });
        navigate({
          pathname: "",
          search: queryParams,
        });
        dispatch(
          getStockOrders({
            query: queryParams,
            view: view,
            processId: processId,
          })
        );
      } else {
        dispatch(
          getStockOrders({ query: query, view: view, processId: processId })
        );
      }
    }
  }, [
    dispatch,
    pageNo,
    perPage,
    orderBy,
    ordr,
    store,
    search,
    dateType,
    startDate,
    endDate,
    tabValue,
    process_action_access,
  ]);

  /**
   * handle what modal to open
   * @param modal - place orer | confirm order | process
   */
  const handleModalToggle = (modal: string) => {
    setModals((prevModals) => ({
      ...prevModals,
      [modal]: !prevModals[modal],
    }));
  };

  /**
   * This handles action button or click on order list or order table
   */
  const handleAction = () => {
    handleModalToggle("processModal");
    dispatch(openStockProcess());
  };

  const filterStoresByIds = (
    stores: ListOfActiveStores[] | undefined,
    storeIds: number[]
  ): ListOfActiveStores[] => {
    return (
      stores?.filter((store) => storeIds.toString().includes(store.store_id)) ??
      []
    );
  };

  const filteredStores: ListOfActiveStores[] = filterStoresByIds(
    getStoreState.data?.stores,
    store ? JSON.parse(store ?? "") : []
  );

  /**
   * Handles tab change
   * @param event - React.SyntheticEvent
   * @param newValue - new tab value
   */
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const params = {
      per_page: perPage,
      tab: newValue,
    };

    const queryParams = createQueryParams(params);
    navigate({
      pathname: "",
      search: queryParams,
    });
  };

  const handleOrderClicked = (id: string) => () => {
    const query = createQueryParams({
      page_no: pageNo,
      per_page: perPage,
      status: status,
      store: store,
      order_by: orderBy,
      order: ordr,
      search: search,
      dateType: dateType,
      startDate: startDate,
      endDate: endDate,
      tab: tabValue,
    });

    navigate({
      pathname: `/admin/material/${view}/${id}`,
      search: query,
    });

    handleAction();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {" "}
      <NoticeBanner />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-gradient-to-r from-red-600 to-orange-600 p-3 rounded-xl shadow-lg">
                {view === "order" && (
                  <HiOutlineShoppingCart className="text-white text-2xl" />
                )}

                {view === "selling" && (
                  <MdOutlineStorefront className="text-white text-2xl" />
                )}
                {view === "procurement" && (
                  <HiOutlineClipboardCheck className="text-white text-2xl" />
                )}
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Stock
                  {view
                    ? " " + view.charAt(0).toUpperCase() + view.slice(1)
                    : "Management"}
                </h1>

                {view === "order" && (
                  <p className="text-gray-600 mt-1">
                    Place stock requests to suppliers with real-time tracking
                    and delivery status visibility.
                  </p>
                )}

                {view === "selling" && (
                  <p className="text-gray-600 mt-1">
                    Manages supplier-side operations, including product catalog,
                    stock control, and dispatching store-placed orders with
                    committed delivery scheduling.
                  </p>
                )}
                {view === "procurement" && (
                  <p className="text-gray-600 mt-1">
                    Oversees delivery verification, ensuring orders arrive on
                    time, complete, and in good condition.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {getStockOrdersState.data ? (
          <div className="bg- border border-gray-200 shadow-lg rounded-t-lg ">
            <Tabs
              className="bg-secondary rounded-t-md text-secondary "
              value={Number(tabValue)}
              onChange={handleTabChange}
              scrollButtons="auto"
              allowScrollButtonsMobile
              variant="scrollable"
              TabIndicatorProps={{
                style: {
                  height: "5px",
                  borderTopLeftRadius: "5px",
                  borderTopRightRadius: "5px",
                  backgroundColor: "white",
                },
              }}
            >
              {process

                ?.filter((fltr) => {
                  /**
                   * Please be sure to put comment if you want to add another condition
                   * Future: This is temporary and should be database driven
                   */

                  // For procurement view only show preparation tab
                  if (view === "procurement") {
                    return fltr.id === 1;
                  }

                  // // Removes delivery receive approval on multim ordering
                  // if (
                  //   view === "order" &&
                  //   getAdminSessionState.data?.admin.user_details.company
                  //     .company_id === 1
                  // ) {
                  //   return fltr.id !== 6 && fltr.id !== 0;
                  // }

                  // Removes the place order tab
                  return fltr.id !== 0;
                })
                .map((tabs, index) => (
                  <Tab
                    className="hidden"
                    key={index}
                    sx={tabStyleSettings(process_action_access, tabs.id)}
                    label={
                      <div className="flex space-x-1 ">
                        <span className="capitalize text-white">
                          {tabs?.name}:
                        </span>
                        <span className="text-[#ffcd17] font-semibold">
                          {getStockOrdersState.data?.tab[index]}
                        </span>
                      </div>
                    }
                  />
                ))}
            </Tabs>

            <TabPanel index={Number(tabValue)} value={Number(tabValue)}>
              <div className="mb-6">
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                  <Button
                    startIcon={<CiFilter className="text-lg" />}
                    variant="contained"
                    onClick={(event) => setOpenFilter(event.currentTarget)}
                    className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-xl px-6 py-3 shadow-lg transform hover:scale-105 transition-all duration-200"
                  >
                    Advanced Filters
                  </Button>

                  {(filteredStores.length > 0 ||
                    (dateType && startDate && endDate)) && (
                    <div className="flex-1 bg-gradient-to-r from-red-50 to-purple-50 rounded-xl p-4 border border-red-200 shadow-sm">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span className="text-sm font-semibold text-gray-700">
                          Active Filters
                        </span>
                      </div>

                      {filteredStores.length > 0 && (
                        <div className="mb-2">
                          <span className="font-medium text-red-700">
                            Stores:{" "}
                          </span>
                          <span className="text-gray-600">
                            {filteredStores.map((row, index) => (
                              <React.Fragment key={row.store_id}>
                                <span className="bg-red-100 text-red-800 px-2 py-1 rounded-md text-xs font-medium mr-1">
                                  {row.name}
                                </span>
                                {index !== filteredStores.length - 1 && (
                                  <span>, </span>
                                )}
                              </React.Fragment>
                            ))}
                          </span>
                        </div>
                      )}

                      {dateType && startDate && endDate && (
                        <div>
                          <span className="font-medium text-red-700 capitalize">
                            {dateType.replace(/_/g, " ")}:
                          </span>
                          <span className="text-gray-600 ml-1">
                            {dateSetup(startDate, false)} -{" "}
                            {dateSetup(endDate, false)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="hidden md:block space-y-3">
                <DataTable
                  columns={columns}
                  order={ordr === "asc" ? "asc" : "desc"}
                  orderBy={orderBy ?? "last_updated"}
                  search={search ?? ""}
                  emptyMessage="Empty"
                  onSearch={(val) => {
                    const params = {
                      page_no: null,
                      per_page: perPage,
                      status: status,
                      order_by: orderBy,
                      order: ordr,
                      store: store,
                      search: val === "" ? null : val,
                      dateType: dateType,

                      startDate: startDate,
                      endDate: endDate,
                      tab: tabValue,
                    };
                    const queryParams = createQueryParams(params);
                    navigate({
                      pathname: "",
                      search: queryParams,
                    });
                  }}
                  onRequestSort={(column_selected) => {
                    if (
                      column_selected !== "action" &&
                      column_selected !== "keyDate" &&
                      column_selected !== "orderStatus"
                    ) {
                      const isAsc =
                        orderBy === column_selected && ordr === "asc";

                      const params = {
                        page_no: pageNo,
                        per_page: perPage,
                        status: status,
                        order_by: column_selected,
                        order: isAsc ? "desc" : "asc",
                        store: store,
                        search: search,
                        dateType: dateType,

                        startDate: startDate,
                        endDate: endDate,
                        tab: tabValue,
                      };

                      const queryParams = createQueryParams(params);

                      dispatch(resetGetStockOrders());
                      navigate({
                        pathname: "",
                        search: queryParams,
                      });
                    }
                  }}
                  onRowsPerPageChange={(event) => {
                    if (perPage !== event.target.value) {
                      const params = {
                        page_no: pageNo,
                        per_page: event.target.value,
                        status: status,
                        store: store,
                        order_by: orderBy,
                        order: ordr,
                        search: search,
                        dateType: dateType,
                        startDate: startDate,
                        endDate: endDate,
                        tab: tabValue,
                      };

                      const queryParams = createQueryParams(params);

                      dispatch(resetGetStockOrders());
                      navigate({
                        pathname: "",
                        search: queryParams,
                      });
                    }
                  }}
                  onPageChange={(event, newPage) => {
                    const pageNoInt = pageNo ? parseInt(pageNo) : null;
                    if (newPage !== pageNoInt) {
                      const params = {
                        page_no: newPage,
                        per_page: perPage,
                        status: status,
                        store: store,
                        order_by: orderBy,
                        order: ordr,
                        search: search,
                        dateType: dateType,

                        startDate: startDate,
                        endDate: endDate,
                        tab: tabValue,
                      };

                      const queryParams = createQueryParams(params);

                      dispatch(resetGetStockOrders());
                      navigate({
                        pathname: "",
                        search: queryParams,
                      });
                    }
                  }}
                  totalRows={getStockOrdersState.data?.pagination.total_rows}
                  perPage={getStockOrdersState.data?.pagination.per_page}
                  page={pageNo ? parseInt(pageNo) : 1}
                >
                  {getStockOrdersState.data?.orders.map((order, index) => (
                    <>
                      {order.parent !== null && (
                        <DataTableRow
                          custom={{
                            // backgroundColor: "#eff6ff",
                            // borderLeft: "4px solid #60a5fa",
                            "& > *": { borderBottom: "none !important" },
                          }}
                        >
                          <DataTableCell colSpan={6} className="!p-1">
                            <button
                              onClick={handleOrderClicked(
                                order.parent?.toString()
                              )}
                              className="text-blue-600 font-medium text-sm self-center hover:underline hover:underline-offset-1 hover:cursor-pointer"
                            >
                              Child order of #{order.parent.toString()}
                            </button>
                          </DataTableCell>
                        </DataTableRow>
                      )}
                      <DataTableRow key={index}>
                        <DataTableCell className="capitalize relative">
                          {order.testMode && (
                            <div className="absolute top-1 left-1 bg-red-600 text-white text-[10px] font-bold px-1.5 py-[2px] rounded-md z-10 shadow-sm">
                              TEST DO NOT PROCESS
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <span>{order.store_name ?? order.companyName}</span>
                          </div>
                        </DataTableCell>
                        <DataTableCell className="capitalize">
                          {order.supplierName}
                        </DataTableCell>
                        <DataTableCell>
                          <div className="relative">
                            <div className="text-center">
                              <div className="text-xs text-gray-500 mb-0.5">
                                ORDER ID
                              </div>
                              <div className="flex justify-center space-x-1">
                                <div className="self-center font-bold text-sm text-gray-900">
                                  #{order.id}
                                </div>
                                {order.discrepancyDate && (
                                  <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center">
                                    <svg
                                      className="w-4 h-4 text-amber-600"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                      />
                                    </svg>
                                  </div>
                                )}
                              </div>

                              {order.category_name && (
                                <div
                                  className={`mt-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                                    order.category_name.toLowerCase() ===
                                    "frozen"
                                      ? "bg-blue-500 text-white"
                                      : "bg-orange-500 text-white"
                                  }`}
                                >
                                  {order.category_name}
                                </div>
                              )}
                            </div>
                          </div>
                        </DataTableCell>
                        <DataTableCell>
                          <div className="relative group inline-block">
                            <span className="inline-block rounded-full text-xs font-medium px-3 py-1 cursor-pointer bg-yellow-100 text-yellow-800">
                              {order.stateFLagName ?? "Pending Action"}
                            </span>

                            <div className="absolute z-10 left-1/2 -translate-x-1/2 mt-1 w-48 text-xs text-white bg-gray-800 rounded px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                              {order.stateFlagDescription ??
                                "Waiting for action"}
                            </div>
                          </div>
                        </DataTableCell>
                        <DataTableCell>
                          <DateTimeline
                            placement={order.order_placement_date}
                            requested={order.requested_delivery_date}
                            committed={order.commited_delivery_date}
                            actual={order.actual_delivery_date}
                            next={order.nextDelivery}
                          />
                        </DataTableCell>
                        <DataTableCell>
                          <IconButton onClick={handleOrderClicked(order.id)}>
                            {order.logistic_id ? (
                              <FcHighPriority className="text-2xl" />
                            ) : (
                              <FaEye className="text-2xl" />
                            )}
                          </IconButton>
                        </DataTableCell>
                      </DataTableRow>
                    </>
                  ))}
                </DataTable>
              </div>

              <div className="block md:hidden space-y-5">
                {/* <Button
                  startIcon={<CiFilter />}
                  variant="contained"
                  onClick={(event) => setOpenFilter(event.currentTarget)}
                >
                  Filter
                </Button> */}
                <DataList
                  search={search ?? ""}
                  emptyMessage={`"No ${
                    TAB_NAVIGATION[Number(tabValue)]?.label
                  } yet."`}
                  onSearch={(val) => {
                    const params = {
                      page_no: null,
                      per_page: perPage,
                      status: status,
                      order_by: orderBy,
                      order: ordr,
                      store: store,
                      search: val === "" ? null : val,
                      dateType: dateType,

                      startDate: startDate,
                      endDate: endDate,
                      tab: tabValue,
                    };

                    const queryParams = createQueryParams(params);

                    navigate({
                      pathname: "",
                      search: queryParams,
                    });
                  }}
                  onRowsPerPageChange={(event) => {
                    if (perPage !== event.target.value) {
                      const params = {
                        page_no: pageNo,
                        per_page: event.target.value,
                        status: status,
                        store: store,
                        order_by: orderBy,
                        order: ordr,
                        search: search,
                        dateType: dateType,

                        startDate: startDate,
                        endDate: endDate,
                        tab: tabValue,
                      };

                      const queryParams = createQueryParams(params);

                      dispatch(resetGetStockOrders());
                      navigate({
                        pathname: "",
                        search: queryParams,
                      });
                    }
                  }}
                  onPageChange={(event, newPage) => {
                    const pageNoInt = pageNo ? parseInt(pageNo) : null;
                    if (newPage !== pageNoInt) {
                      const params = {
                        page_no: newPage,
                        per_page: perPage,
                        status: status,
                        store: store,
                        order_by: orderBy,
                        order: ordr,
                        search: search,
                        dateType: dateType,

                        startDate: startDate,
                        endDate: endDate,
                        tab: tabValue,
                      };

                      const queryParams = createQueryParams(params);

                      dispatch(resetGetStockOrders());
                      navigate({
                        pathname: "",
                        search: queryParams,
                      });
                    }
                  }}
                  totalRows={getStockOrdersState.data?.pagination.total_rows}
                  perPage={getStockOrdersState.data?.pagination.per_page}
                  page={pageNo ? parseInt(pageNo) : 1}
                >
                  <div className="space-y-2 mt-2">
                    {getStockOrdersState.data.orders.map((order) => (
                      <div
                        key={order.id}
                        className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-sm transition-shadow"
                      >
                        {/* Parent Order Reference */}
                        {order.parent !== null && (
                          <div className="px-3 py-1.5 bg-blue-50 border-b border-blue-100">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const query = createQueryParams({
                                  page_no: pageNo,
                                  per_page: perPage,
                                  status: status,
                                  store: store,
                                  order_by: orderBy,
                                  order: ordr,
                                  search: search,
                                  dateType: dateType,
                                  startDate: startDate,
                                  endDate: endDate,
                                  tab: tabValue,
                                });

                                navigate({
                                  pathname: `/admin/material/${view}/${order.parent}`,
                                  search: query,
                                });
                              }}
                              className="text-blue-600 text-[11px] font-medium hover:text-blue-700"
                            >
                              Child order of #{order.parent.toString()}
                            </button>
                          </div>
                        )}

                        {/* Main Content */}
                        <div
                          onClick={() => {
                            const query = createQueryParams({
                              page_no: pageNo,
                              per_page: perPage,
                              status: status,
                              store: store,
                              order_by: orderBy,
                              order: ordr,
                              search: search,
                              dateType: dateType,
                              startDate: startDate,
                              endDate: endDate,
                              tab: tabValue,
                            });

                            navigate({
                              pathname: `/admin/material/${view}/${order.id}`,
                              search: query,
                            });

                            handleAction();
                          }}
                          className="p-3 cursor-pointer active:bg-gray-50 transition-colors"
                        >
                          {/* Header Row */}
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <div className="w-6 h-6 bg-red-100 rounded flex items-center justify-center flex-shrink-0">
                                  <span className="text-red-600 text-xs font-bold">
                                    {(order.store_name ?? order.companyName)
                                      .charAt(0)
                                      .toUpperCase()}
                                  </span>
                                </div>
                                <h3 className="font-semibold text-sm text-gray-900 capitalize leading-tight truncate">
                                  {order.store_name ?? order.companyName}
                                </h3>
                              </div>
                            </div>

                            <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                              <div className="flex items-center gap-1">
                                <span className="text-[10px] text-gray-500">
                                  ID
                                </span>
                                <span className="text-xs font-bold text-gray-900">
                                  #{order.id}
                                </span>
                                {order.discrepancyDate && (
                                  <div className="w-4 h-4 bg-amber-50 border border-amber-200 rounded flex items-center justify-center">
                                    <svg
                                      className="w-2.5 h-2.5 text-amber-600"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                      />
                                    </svg>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-0.5">
                                {order.testMode && (
                                  <div className="bg-red-500 text-white text-[7px] font-bold px-1 py-0.5 rounded uppercase">
                                    TEST
                                  </div>
                                )}
                                {order.logistic_id && (
                                  <FcHighPriority className="text-base" />
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Supplier, Category & Status Row */}
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-2">
                            <div className="flex items-center gap-1 text-[11px]">
                              <span className="text-gray-500 uppercase font-medium">
                                Supplier
                              </span>
                              <span className="font-semibold text-gray-900 capitalize">
                                {order.supplierName}
                              </span>
                            </div>

                            {order.category_name && (
                              <div className="flex items-center gap-1 text-[11px]">
                                <span className="text-gray-500 uppercase font-medium">
                                  Category
                                </span>
                                <span
                                  className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                                    order.category_name.toLowerCase() ===
                                    "frozen"
                                      ? "bg-blue-500 text-white"
                                      : "bg-orange-500 text-white"
                                  }`}
                                >
                                  {order.category_name}
                                </span>
                              </div>
                            )}

                            <div className="flex items-center gap-1 text-[11px]">
                              <span className="text-gray-500 uppercase font-medium">
                                Status
                              </span>
                              <span className="font-semibold px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-700">
                                {order.stateFLagName ?? "Pending Action"}
                              </span>
                            </div>
                          </div>

                          {/* Delivery Timeline */}
                          <div className="bg-gray-50 rounded p-2">
                            <div className="text-[10px] font-semibold text-gray-700 uppercase mb-1.5 flex items-center gap-1">
                              <svg
                                className="w-3 h-3"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                              </svg>
                              Delivery Timeline
                            </div>
                            <div className="grid grid-cols-2 gap-1.5">
                              {order.order_placement_date && (
                                <div>
                                  <div className="text-[9px] text-gray-500 uppercase font-medium mb-0.5">
                                    Requested
                                  </div>
                                  <div className="text-[11px] text-gray-900 font-semibold">
                                    {dateSetup(
                                      order.order_placement_date,
                                      true
                                    )}
                                  </div>
                                </div>
                              )}

                              {order.commited_delivery_date && (
                                <div>
                                  <div className="text-[9px] text-gray-500 uppercase font-medium mb-0.5">
                                    Committed
                                  </div>
                                  <div className="text-[11px] text-gray-900 font-semibold">
                                    {dateSetup(
                                      order.commited_delivery_date,
                                      true
                                    )}
                                  </div>
                                </div>
                              )}

                              {order.actual_delivery_date ? (
                                <div className="col-span-2 pt-1.5 border-t border-gray-200">
                                  <div className="text-[9px] text-green-600 uppercase font-bold mb-0.5">
                                    Actual Delivery
                                  </div>
                                  <div className="text-[11px] text-green-700 font-bold">
                                    {dateSetup(
                                      order.actual_delivery_date,
                                      true
                                    )}
                                  </div>
                                </div>
                              ) : (
                                order.requested_delivery_date && (
                                  <div className="col-span-2 pt-1.5 border-t border-gray-200">
                                    <div className="text-[9px] text-blue-600 uppercase font-bold mb-0.5">
                                      Expected Delivery
                                    </div>
                                    <div className="text-[11px] text-blue-700 font-bold">
                                      {dateSetup(
                                        order.requested_delivery_date,
                                        true
                                      )}
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </DataList>
              </div>
            </TabPanel>
          </div>
        ) : null}
      </div>
      <OrderFilter
        anchor={openFilter}
        onClose={() => setOpenFilter(null)}
        filter={(data: DataFilterData | string) => {
          if (typeof data !== "string") {
            const storeIds = data.store?.map((store) => store[0].store_id);

            const params = {
              page_no: null,
              per_page: perPage,
              status: status,
              order_by: orderBy,
              order: ordr,
              store: storeIds ? JSON.stringify(storeIds) : null,
              search: search,
              dateType: data.type ?? null,
              startDate: data.start ?? null,
              endDate: data.end ?? null,
              tab: tabValue,
            };
            const queryParams = createQueryParams(params);
            navigate({
              pathname: "",
              search: queryParams,
            });
          } else {
            navigate({
              pathname: "",
            });
          }
        }}
      />
      <FloatingPlaceOrder
        actionProcess={process_action_access}
        view={view}
        modals={modals}
        dispatch={dispatch}
        handleModalToggle={handleModalToggle}
      />
      <PlaceOrderModal
        open={modals.placeOrder}
        onClose={() => {
          handleModalToggle("placeOrder");
        }}
        openConfirmationState={() => handleModalToggle("confirmOrder")}
      />
      <ConfirmOrdersModal
        open={modals.confirmOrder}
        onClose={() => handleModalToggle("confirmOrder")}
        openPlaceOrderModal={() => handleModalToggle("placeOrder")}
      />
      <ProcessModal
        open={modals.processModal}
        onClose={() => {
          handleModalToggle("processModal");
          dispatch(resetGetProductData());
        }}
      />
    </div>
  );
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <div>{children}</div>
        </Box>
      )}
    </div>
  );
};

const FloatingPlaceOrder = (props: FloatingPlaceOrderProps) => {
  const { actionProcess, view, modals, dispatch, handleModalToggle } = props;

  const isPlaceOrderAvailable = actionProcess?.some(
    (perm) => perm.status_id === 0 && view === "order"
  );

  const modalIsOpen = Object.keys(modals)
    .map((key) => modals[key])
    .some((modal) => modal);
  if (isPlaceOrderAvailable && !modalIsOpen) {
    return (
      <SpeedDial
        ariaLabel={"speed-dial-finance-and-store"}
        sx={{
          position: "absolute",
          bottom: 40,
          right: 40,
          zIndex: 1,
        }}
        icon={<SpeedDialIcon />}
      >
        <SpeedDialAction
          icon={<TiDocumentAdd className="text-3xl" />}
          tooltipTitle="Place Order"
          onClick={() => {
            dispatch(adminSettingsEditUserMaterialSupplierCustomer());
            handleModalToggle("placeOrder");
          }}
        />
      </SpeedDial>
    );
  }

  return null;
};
const DateTimeline = (props: DateTimelineProps) => {
  const { placement, requested, committed, actual, next } = props;

  const steps = [
    {
      label: "Order Placed",
      date: placement,
      icon: <CiCalendar className="text-sky-600 w-5 h-5" />,
    },
    {
      label: "Requested Delivery",
      date: requested,
      icon: <CiCalendar className="text-orange-500 w-5 h-5" />,
    },
    {
      label: "Next Delivery",
      date: next,
      icon: <CiCalendar className="text-orange-500 w-5 h-5" />,
    },
    {
      label: "Committed Delivery",
      date: committed,
      icon: <CiCalendar className="text-violet-500 w-5 h-5" />,
    },
    {
      label: "Delivered",
      date: actual,
      icon: <CiCalendar className="text-emerald-600 w-5 h-5" />,
    },
  ];

  return (
    <div className="flex flex-col gap-3 bg-white p-4 rounded-lg shadow-sm text-[11px] font-[Roboto]">
      {steps.map(
        (step, index) =>
          step.date && (
            <div key={index} className="flex items-start gap-2">
              <div className="pt-0.5">{step.icon}</div>
              <div>
                <div className="text-gray-800 font-medium">{step.label}</div>
                <div className="text-gray-500">
                  {/** Enables time on order placement */}
                  {dateSetup(step.date, index === 0 ? true : false)}
                </div>
              </div>
            </div>
          )
      )}
    </div>
  );
};
